import { Dashboard } from "api/model/dashboard/Dashboard";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { TreeNode } from "primereact/treenode";
import { TreeSelect, TreeSelectChangeEvent } from "primereact/treeselect";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import "../dashboard-styles/DashboardMain.scss";
import {
  filterTreeNodes,
  getFlatNodeMap,
  getFlatTabList,
  getMenuItems,
  getTreeSelectOptions,
} from "../utils";

interface DashboardTabsDropdownMenuProps {
  isMobile: boolean;
  menuRef: React.RefObject<Menu | null>;
  tabId: number;
  dashboard: Dashboard;
}

enum ArrowDirection {
  UP = "up",
  DOWN = "down",
}

type NodeKey = Record<string, boolean>;

const DashboardTabsDropdownMenu = ({
  isMobile,
  menuRef,
  tabId,
  dashboard,
}: DashboardTabsDropdownMenuProps) => {
  const [filterValue, setFilterValue] = useState<string>("");
  const navigate = useNavigate();

  const selectedTabUUID = useMemo(() => {
    const currentTab = dashboard?.dashboardTabsJs?.[tabId];
    return currentTab ? currentTab.uuid : null;
  }, [dashboard?.dashboardTabsJs, tabId]);

  const handleTabChange = (tabUUID: string, e?: TreeSelectChangeEvent | { originalEvent?: Event }) => {
    const selectedIndex = dashboard.dashboardTabsJs.findIndex(tab => tab.uuid === tabUUID);

    const isCtrlOrMetaPressed =
      e?.originalEvent &&
      ("ctrlKey" in e.originalEvent || "metaKey" in e.originalEvent) &&
      ((e.originalEvent as MouseEvent).ctrlKey || (e.originalEvent as MouseEvent).metaKey);

    if (!isMobile && isCtrlOrMetaPressed) {
      const newTabUrl = `${window.location.pathname}/#/dashboard/${dashboard.id}/tab/${selectedIndex}`;
      window.open(newTabUrl, "_blank");
    } else {
      const url = `/dashboard/${dashboard.id}/tab/${selectedIndex}`;
      navigate(url);
    }
  };

  const changeTabWithArrow = (direction: ArrowDirection) => {
    const currentIndex = flatTabNodes.findIndex(node => node.key === selectedTabUUID);
    if (currentIndex === -1) return;

    const newIndex = direction === ArrowDirection.DOWN ? currentIndex + 1 : currentIndex - 1;

    if (newIndex < 0 || newIndex >= flatTabNodes.length) return;

    handleTabChange(flatTabNodes[newIndex].key as string);
  };

  const treeOptions = useMemo(
    () => getTreeSelectOptions(dashboard.dashboardTabsJs, dashboard.tabGroups ?? []),
    [dashboard.dashboardTabsJs, dashboard.tabGroups]
  );

  const flatTabNodes = useMemo(() => getFlatTabList(treeOptions), [treeOptions]);

  const flatNodeMap = useMemo(() => getFlatNodeMap(treeOptions), [treeOptions]);

  const getExpandedKeys = (): NodeKey => {
    const keys: NodeKey = {};
    if (!selectedTabUUID) return keys;

    const findParentKey = (nodes: TreeNode[]): string | null =>
      nodes.reduce<string | null>((found, node) => {
        if (found) return found;

        if (node.children?.some(child => child.key === selectedTabUUID)) {
          return node.key as string;
        }

        return node.children ? findParentKey(node.children) : null;
      }, null);

    const parentKey = findParentKey(treeOptions);
    if (parentKey) {
      keys[parentKey] = true;
    }

    return keys;
  };

  const [expandedKeys, setExpandedKeys] = useState<NodeKey>(getExpandedKeys());

  const handleTreeSelectChange = (e: TreeSelectChangeEvent, currentFlatNodeMap: Record<string, TreeNode>) => {
    const selectedValue = e.value as string;
    const selectedNode = currentFlatNodeMap[selectedValue];

    if (selectedNode?.children) {
      setExpandedKeys(prev => ({
        ...prev,
        [selectedValue]: !prev[selectedValue],
      }));
      return;
    }

    handleTabChange(selectedValue, e);
  };

  const nodeTemplate = (node: TreeNode) => {
    const isLeaf = !node.children;

    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();

      if (isLeaf) {
        handleTabChange(node.key as string);
      } else {
        setExpandedKeys(prev => {
          const currentlyExpanded = !!prev[node.key as string];
          const newExpandedKeys = { ...prev };

          if (currentlyExpanded) {
            delete newExpandedKeys[node.key as string];
          } else {
            newExpandedKeys[node.key as string] = true;
          }

          return newExpandedKeys;
        });
      }
    };

    return (
      <span
        onClick={handleClick}
        className={`inline-block h-2rem line-height-4 w-full cursor-pointer font-${isLeaf ? "normal" : "bold"}`}
      >
        {node.label}
      </span>
    );
  };

  const valueTemplate = (node: TreeNode | TreeNode[] | undefined): string => {
    if (!node) return "";
    if (Array.isArray(node)) return node[0]?.label ?? "";
    return node.label ?? "";
  };

  const getExpandedKeysForFilter = (nodes: TreeNode[], filter: string): NodeKey => {
    const keys: NodeKey = {};
    if (!filter) return keys;

    const traverse = (nodeList: TreeNode[]) => {
      nodeList.forEach(node => {
        const matchedChildren = node.children ? filterTreeNodes(node.children, filter) : [];

        if (node.children && matchedChildren.length > 0) {
          keys[node.key as string] = true;
          traverse(node.children);
        }
      });
    };

    traverse(nodes);
    return keys;
  };

  const computedExpandedKeys =
    filterValue && filterValue.trim().length > 0
      ? getExpandedKeysForFilter(treeOptions, filterValue)
      : expandedKeys;

  const filterTemplate = () => (
    <div className="p-treeselect-filter-container">
      <input
        type="text"
        className="p-treeselect-filter p-inputtext p-component"
        placeholder={T("action.search")}
        value={filterValue}
        onChange={e => setFilterValue(e.target.value)}
      />
    </div>
  );

  return (
    <div>
      {isMobile ? (
        <Menu
          ref={menuRef}
          model={getMenuItems(treeOptions, handleTabChange)}
          popup
          className="w-12 menu-list"
        />
      ) : (
        <div className="flex flex-row gap-2 items-center tabs-dropdown">
          <TreeSelect
            value={selectedTabUUID}
            options={filterTreeNodes(treeOptions, filterValue)}
            onChange={(e: TreeSelectChangeEvent) => handleTreeSelectChange(e, flatNodeMap)}
            onToggle={e => setExpandedKeys(e.value)}
            className="w-21rem"
            filter
            filterTemplate={filterTemplate}
            onHide={() => setFilterValue("")}
            placeholder={T("action.select-tab")}
            selectionMode="single"
            valueTemplate={(node: TreeNode | TreeNode[]) => valueTemplate(node)}
            nodeTemplate={nodeTemplate}
            expandedKeys={computedExpandedKeys}
          />
          {dashboard.showTabNavigation && (
            <>
              <Button
                icon="pi pi-arrow-left"
                className="p-button-text"
                disabled={flatTabNodes.findIndex(node => node.key === selectedTabUUID) <= 0}
                tooltip={T("action.select-previous-tab")}
                tooltipOptions={{ position: "bottom" }}
                onClick={() => changeTabWithArrow(ArrowDirection.UP)}
              />
              <Button
                icon="pi pi-arrow-right"
                className="p-button-text"
                disabled={
                  flatTabNodes.findIndex(node => node.key === selectedTabUUID) === flatTabNodes.length - 1
                }
                tooltip={T("action.select-next-tab")}
                tooltipOptions={{ position: "bottom" }}
                onClick={() => changeTabWithArrow(ArrowDirection.DOWN)}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardTabsDropdownMenu;
